import { React, type FC } from '../types/react-imports';
import { HeaderProps } from '@/types/components';
import { SOLVRAYS_LOGO, COMPANY } from '../lib/constant';

const Header: FC<HeaderProps> = () => {
  const [isScrolled, setIsScrolled] = React.useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);

  React.useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Prevent body scroll when mobile menu is open
  React.useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <header
      className={`
        fixed top-0 left-0 right-0 z-50
        ${isScrolled ? 'bg-black/30 backdrop-blur-sm' : 'bg-transparent'}
        transition-all duration-300
      `}
    >
      <div className="max-w-[1400px] mx-auto">
        <div className="flex items-center justify-between px-6 py-3">
          {/* Logo */}
          <div className="flex items-center gap-2">
            <img 
              src={SOLVRAYS_LOGO.DEFAULT} 
              alt="Solvrays Logo" 
              className="h-12 w-auto"
            />
          </div>

          {/* Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            <a 
              href="#ai-genes" 
              className="text-gray-300 hover:text-white transition-colors text-xl font-medium"
            >
              AI Genes
            </a>
            <a 
              href="#transformations" 
              className="text-gray-300 hover:text-white transition-colors text-xl font-medium"
            >
             Transformations
            </a>
            <a 
              href="#team" 
              className="text-gray-300 hover:text-white transition-colors text-xl font-medium"
            >
              Team
            </a>
          </nav>

          {/* CTA Button */}
          <div className="flex items-center gap-4">
            <a
              href="#contact"
              className="
                hidden md:inline-flex items-center
                px-8 py-3 rounded-full
                bg-gradient-to-r from-blue-500 to-emerald-500
                hover:from-blue-600 hover:to-emerald-600
                text-white text-xl font-medium
                transition-all duration-300
                shadow-[0_0_20px_rgba(59,130,246,0.3)]
                hover:shadow-[0_0_25px_rgba(59,130,246,0.4)]
              "
            >
              Book a Demo
            </a>
            
            {/* Mobile Menu Button */}
            <button
              className="md:hidden relative group p-2"
              onClick={toggleMobileMenu}
              aria-label="Menu"
            >
              <div className="flex flex-col justify-center items-center w-6 h-6">
                <span className={`
                  block w-5 h-0.5 bg-white
                  transition-all duration-300 ease-out
                  group-hover:bg-blue-400
                  ${isMobileMenuOpen ? 'rotate-45 translate-y-[0.3rem]' : ''}
                `}></span>
                <span className={`
                  block w-5 h-0.5 bg-white mt-1
                  transition-all duration-300 ease-out
                  group-hover:bg-blue-400
                  ${isMobileMenuOpen ? '-rotate-45 -translate-y-[0.2rem]' : ''}
                `}></span>
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      <div className={`
        fixed inset-0 bg-black/95 backdrop-blur-lg
        flex flex-col items-center justify-center
        transition-all duration-300
        ${isMobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}
      `}>
        <nav className="flex flex-col items-center gap-8">
          <a 
            href="#ai-genes"
            className="text-2xl text-gray-300 hover:text-white transition-colors"
            onClick={closeMobileMenu}
          >
            AI Genes
          </a>
          <a 
            href="#transformations"
            className="text-2xl text-gray-300 hover:text-white transition-colors"
            onClick={closeMobileMenu}
          >
            Transformations
          </a>
          <a 
            href="#team"
            className="text-2xl text-gray-300 hover:text-white transition-colors"
            onClick={closeMobileMenu}
          >
            Team
          </a>
          <a
            href="#contact"
            className="
              mt-4 px-8 py-3 rounded-full
              bg-gradient-to-r from-blue-500 to-emerald-500
              hover:from-blue-600 hover:to-emerald-600
              text-white text-lg font-medium
              transition-all duration-300
              shadow-[0_0_20px_rgba(59,130,246,0.3)]
              hover:shadow-[0_0_25px_rgba(59,130,246,0.4)]
            "
            onClick={closeMobileMenu}
          >
            Book a Demo
          </a>
        </nav>
      </div>
    </header>
  );
};

export default Header;
